import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import { marketingApi } from '@/services/api';
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Loader2,
  Mail,
  Menu,
  MessageCircle,
  Smartphone,
  XCircle,
} from 'lucide-react';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
] as const;

const CHANNEL_ICON = {
  WhatsApp: MessageCircle,
  SMS: Smartphone,
  Email: Mail,
} as const;

const PURPOSE_LABEL: Record<string, string> = {
  order_notify: 'Order notifications',
  campaign: 'Campaigns',
  test: 'Tests',
  other: 'Other',
};

type ChannelRow = {
  channel: string;
  sent: number;
  failed: number;
  attempts: number;
  tests: number;
};

export default function MarketingUsagePage() {
  const { toggleSidebar } = useUIStore();
  const [period, setPeriod] = useState<(typeof PERIODS)[number]['id']>('30d');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const queryPeriod = period === 'custom' ? undefined : period;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['marketingUsage', period, fromDate, toDate],
    queryFn: async () =>
      (
        await marketingApi.getUsageReport({
          period: queryPeriod,
          from_date: period === 'custom' ? fromDate || undefined : undefined,
          to_date: period === 'custom' ? toDate || undefined : undefined,
        })
      ).data.message,
    enabled: period !== 'custom' || Boolean(fromDate && toDate),
  });

  const channels: ChannelRow[] = data?.by_channel || [];
  const maxSent = useMemo(
    () => Math.max(1, ...channels.map((c) => c.sent || 0)),
    [channels]
  );

  return (
    <div className="flex h-screen w-full max-w-[100%] overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={toggleSidebar}
            className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link
            to="/settings?tab=marketing"
            className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Marketing
          </Link>
          <h1 className="text-lg font-semibold text-gray-900">Message usage</h1>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-600 max-w-2xl">
                Messages successfully sent on WhatsApp, SMS and Email. Use this to track how many
                messages you have consumed so far.
              </p>
              {data?.from_date && (
                <p className="text-xs text-gray-500 mt-1">
                  Period: {data.from_date} → {data.to_date}
                  {isFetching ? ' · refreshing…' : ''}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriod(p.id)}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${
                    period === p.id
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {period === 'custom' && (
            <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-xl p-3">
              <label className="text-sm">
                <span className="text-gray-600 text-xs">From</span>
                <input
                  type="date"
                  className="mt-1 block border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-600 text-xs">To</span>
                <input
                  type="date"
                  className="mt-1 block border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={!fromDate || !toDate}
                onClick={() => refetch()}
              >
                Apply
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading usage…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Sent (consumed)', value: data?.totals?.sent ?? 0, tone: 'text-emerald-700' },
                  { label: 'Failed', value: data?.totals?.failed ?? 0, tone: 'text-rose-700' },
                  { label: 'Attempts', value: data?.totals?.attempts ?? 0, tone: 'text-gray-900' },
                  { label: 'Test sends', value: data?.totals?.tests ?? 0, tone: 'text-sky-700' },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="bg-white border border-gray-200 rounded-xl px-4 py-3"
                  >
                    <p className="text-xs text-gray-500">{s.label}</p>
                    <p className={`text-2xl font-semibold tabular-nums mt-1 ${s.tone}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-gray-500" />
                  <h2 className="font-semibold text-gray-900">By channel</h2>
                </div>
                <div className="space-y-4">
                  {channels.map((row) => {
                    const Icon =
                      CHANNEL_ICON[row.channel as keyof typeof CHANNEL_ICON] || MessageCircle;
                    const pct = Math.round(((row.sent || 0) / maxSent) * 100);
                    return (
                      <div key={row.channel} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-4 h-4 text-gray-600 shrink-0" />
                            <span className="text-sm font-medium text-gray-900">{row.channel}</span>
                          </div>
                          <div className="text-xs text-gray-600 tabular-nums shrink-0">
                            <span className="text-emerald-700 font-semibold">{row.sent}</span> sent
                            <span className="mx-1.5 text-gray-300">·</span>
                            {row.failed} failed
                            <span className="mx-1.5 text-gray-300">·</span>
                            {row.tests} tests
                          </div>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-teal-500 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {(data?.by_purpose || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                  <h2 className="font-semibold text-gray-900 mb-3">By purpose</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          <th className="pb-2 font-medium">Purpose</th>
                          <th className="pb-2 font-medium text-right">Sent</th>
                          <th className="pb-2 font-medium text-right">Failed</th>
                          <th className="pb-2 font-medium text-right">Attempts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.by_purpose as Array<Record<string, number | string>>).map((p) => (
                          <tr key={String(p.purpose)} className="border-b border-gray-50">
                            <td className="py-2 text-gray-900">
                              {PURPOSE_LABEL[String(p.purpose)] || String(p.purpose)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-emerald-700">
                              {p.sent}
                            </td>
                            <td className="py-2 text-right tabular-nums text-rose-700">
                              {p.failed}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-700">
                              {p.attempts}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(data?.by_day || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                  <h2 className="font-semibold text-gray-900 mb-3">Daily breakdown</h2>
                  <div className="overflow-x-auto max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          <th className="pb-2 font-medium">Date</th>
                          <th className="pb-2 font-medium text-right">Sent</th>
                          <th className="pb-2 font-medium text-right">Failed</th>
                          <th className="pb-2 font-medium text-right">Attempts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.by_day as Array<Record<string, number | string>>).map((d) => (
                          <tr key={String(d.day)} className="border-b border-gray-50">
                            <td className="py-2 text-gray-900 tabular-nums">{d.day}</td>
                            <td className="py-2 text-right tabular-nums text-emerald-700">
                              {d.sent}
                            </td>
                            <td className="py-2 text-right tabular-nums text-rose-700">
                              {d.failed}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-700">
                              {d.attempts}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                <h2 className="font-semibold text-gray-900 mb-3">Recent messages</h2>
                {(data?.recent || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No messages logged in this period yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          <th className="pb-2 font-medium">When</th>
                          <th className="pb-2 font-medium">Channel</th>
                          <th className="pb-2 font-medium">Purpose</th>
                          <th className="pb-2 font-medium">Recipient</th>
                          <th className="pb-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(
                          data.recent as Array<{
                            name: string;
                            creation: string;
                            channel: string;
                            purpose: string;
                            recipient: string;
                            success: number;
                            dry_run: number;
                            error: string;
                          }>
                        ).map((r) => (
                          <tr key={r.name} className="border-b border-gray-50 align-top">
                            <td className="py-2 text-xs text-gray-600 tabular-nums whitespace-nowrap">
                              {r.creation?.replace('T', ' ').slice(0, 19)}
                            </td>
                            <td className="py-2 text-gray-900">{r.channel}</td>
                            <td className="py-2 text-gray-700">
                              {PURPOSE_LABEL[r.purpose] || r.purpose || 'Other'}
                            </td>
                            <td className="py-2 text-gray-700 max-w-[180px] truncate" title={r.recipient}>
                              {r.recipient || '—'}
                            </td>
                            <td className="py-2">
                              {r.dry_run ? (
                                <span className="inline-flex items-center gap-1 text-xs text-sky-700">
                                  Test / dry-run
                                </span>
                              ) : r.success ? (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Sent
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-rose-700"
                                  title={r.error || ''}
                                >
                                  <XCircle className="w-3.5 h-3.5" /> Failed
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
